import React, { useState, useEffect } from 'react';
import { Card, Form, InputNumber, Select, Button, Table, Space, Tag, Modal, message, Statistic, Row, Col } from 'antd';
import { EditOutlined, CheckCircleOutlined, LockOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { getCurrentTaskYear, updateTaskYear, publishTaskYear, unpublishTaskYear, generateQuarters } from '../../../services/api';

export default function YearlyTarget() {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editAnnual, setEditAnnual] = useState(false);
  const [annualForm] = Form.useForm();

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const years = [];
  for (let y = currentYear; y <= currentYear + 2; y++) years.push(y);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getCurrentTaskYear(selectedYear);
      setRecord(res.data);
    } catch (err) { message.error(err.message); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [selectedYear]);

  const handleSaveAnnual = async () => {
    const { annualAmount } = await annualForm.validateFields();
    try {
      const res = await updateTaskYear(record.id, { annualAmount });
      setRecord(res.data);
      setEditAnnual(false);
      message.success('保存成功');
    } catch (err) { message.error(err.message); }
  };

  const handlePublish = async () => {
    try {
      await publishTaskYear(record.id);
      fetchData();
      message.success('已发布');
    } catch (err) { message.error(err.message); }
  };

  const handleUnpublish = async () => {
    try {
      await unpublishTaskYear(record.id);
      fetchData();
      message.success('已取消发布');
    } catch (err) { message.error(err.message); }
  };

  const handleGenerate = async () => {
    Modal.confirm({
      title: '重置季度分配',
      content: '将Q1-Q4改为均分，已调整的数据将丢失，确定？',
      onOk: async () => {
        try {
          const res = await generateQuarters(record.id);
          setRecord(res.data);
          message.success('已重置为均分');
        } catch (err) { message.error(err.message); }
      },
    });
  };

  const handleQuarterChange = async (quarter, value) => {
    const quarters = (record.quarters || []).map(q => ({
      quarter: q.quarter,
      quarterAmount: q.quarter === quarter ? value : q.quarterAmount,
    }));
    try {
      const res = await updateTaskYear(record.id, { quarters });
      setRecord(res.data);
    } catch (err) { message.error(err.message); }
  };

  const annualAmount = record ? Number(record.annualAmount) : 0;
  const quarters = (record?.quarters || []).sort((a, b) => a.quarter - b.quarter);
  const totalQuarters = quarters.reduce((s, q) => s + Number(q.quarterAmount), 0);
  const ratio = annualAmount > 0 ? (totalQuarters / annualAmount * 100) : 0;
  const isPublished = record?.status === 1;

  const quarterColumns = [
    { title: '季度', dataIndex: 'quarter', render: (v) => `Q${v}` },
    {
      title: '任务金额', dataIndex: 'quarterAmount',
      render: (v, r) => isPublished ? `¥${Number(v).toLocaleString()}` : (
        <InputNumber
          value={Number(v)}
          min={0}
          style={{ width: 180 }}
          formatter={(val) => `¥ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(val) => val.replace(/[¥,\s]/g, '')}
          onChange={(val) => handleQuarterChange(r.quarter, val || 0)}
          disabled={isPublished}
        />
      ),
    },
    {
      title: '占比', render: (_, r) => {
        const pct = annualAmount > 0 ? (Number(r.quarterAmount) / annualAmount * 100) : 0;
        return `${pct.toFixed(2)}%`;
      },
    },
  ];

  const isInRange = totalQuarters >= annualAmount * 0.8;

  return (
    <div>
      <Card
        title={
          <Space>
            <span>年度目标设定</span>
            <Select value={selectedYear} onChange={setSelectedYear} style={{ width: 100 }}>
              {years.map(y => <Select.Option key={y} value={y}>{y}年</Select.Option>)}
            </Select>
            {record && (
              <Tag color={isPublished ? 'green' : 'orange'}>{isPublished ? '已发布' : '草稿'}</Tag>
            )}
          </Space>
        }
        extra={
          record && !isPublished ? (
            <Space>
              <Button onClick={handleGenerate} icon={<MinusCircleOutlined />}>重置为均分</Button>
              <Button type="primary" icon={<CheckCircleOutlined />} onClick={handlePublish}>发布</Button>
            </Space>
          ) : record && (
            <Button icon={<LockOutlined />} onClick={handleUnpublish}>取消发布</Button>
          )
        }
      >
        <Row gutter={24} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Statistic title="年度任务总额" value={annualAmount} suffix="元"
              formatter={(v) => `¥${Number(v).toLocaleString()}`}
            />
            {!isPublished && (
              <Button size="small" type="link" icon={<EditOutlined />}
                onClick={() => { annualForm.setFieldsValue({ annualAmount }); setEditAnnual(true); }}
              >修改</Button>
            )}
          </Col>
          <Col span={6}>
            <Statistic title="Q1-Q4 合计" value={totalQuarters} suffix="元"
              formatter={(v) => `¥${Number(v).toLocaleString()}`}
            />
          </Col>
          <Col span={6}>
            <Statistic title="分配占比" value={ratio.toFixed(2)} suffix="%"
              valueStyle={{ color: isInRange ? '#52c41a' : '#ff4d4f' }}
            />
            {record && totalQuarters > 0 && (
              <div style={{ fontSize: 12, color: isInRange ? '#52c41a' : '#ff4d4f' }}>
                {isInRange ? '✅ 已达80%以上' : '⚠️ 未达80%下限'}
              </div>
            )}
          </Col>
          <Col span={6}>
            <Statistic title="状态" value={isPublished ? '已发布' : '草稿'}
              valueStyle={{ color: isPublished ? '#52c41a' : '#faad14' }}
            />
          </Col>
        </Row>

        <Table
          dataSource={quarters}
          columns={quarterColumns}
          rowKey="quarter"
          pagination={false}
          loading={loading}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}><strong>合计</strong></Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <strong>¥{totalQuarters.toLocaleString()}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2}>
                <strong style={{ color: isInRange ? '#52c41a' : '#ff4d4f' }}>
                  {ratio.toFixed(2)}% {isInRange ? '✅' : '⚠️'}
                </strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      </Card>

      <Modal title="修改年度任务总额" open={editAnnual} onOk={handleSaveAnnual}
        onCancel={() => setEditAnnual(false)}
      >
        <Form form={annualForm} layout="vertical">
          <Form.Item name="annualAmount" label="年度任务总额" rules={[{ required: true, message: '请输入' }]}>
            <InputNumber min={0} style={{ width: '100%' }}
              formatter={(val) => `¥ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(val) => val.replace(/[¥,\s]/g, '')}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
